/**
 * Project Configuration System
 *
 * Simple, non-abstract configuration for project-specific features.
 * Each project defines what components and features it needs.
 */

export interface ProjectConfig {
  id: string;
  name: string;
  slug: string;
  description: string;

  // Core components - simple booleans
  features: {
    piAlignment: boolean;
    systemArchitecture: boolean;
    auditViewer: boolean;
    faqAssist: boolean;
    nocMonitoring: boolean;
  };

  // Project-specific data viewer configuration
  dataViewer?: {
    enabled: boolean;
    menuLabel: string;  // "Audit Viewer", "PDT JSON Viewer", etc.
    recordTypeLabel: string;  // "Audit Summary", "PDT Record", etc.
    dataType: 'pvq-audit' | 'pdt-record' | 'custom';
    samplePath?: string;
    description?: string;
    samplePayloads?: Array<{
      id: string;
      title: string;
      description: string;
      icon: 'check' | 'alert' | 'document' | 'shield';
      color: 'green' | 'red' | 'blue' | 'yellow';
      path: string;
    }>;
  };

  // Analytics configuration - points to project-specific parquet files
  analytics?: {
    enabled: boolean;
    dataLakePath?: string;
    parquetPrefix?: string;
    athenaTable?: string;  // Athena table name (e.g., 'audit_events', 'pdt_events')
  };

  // Sidebar Form Sections - project-specific form sections shown in sidebar
  sidebarSections?: {
    enabled: boolean;
    title: string;  // e.g., "PVQ Sections", "PDT Sections", "Forms"
    sections: Array<{
      id: string;
      label: string;
      icon: string | React.ReactNode;
      order: number;
      badge?: string;  // e.g., "AI", "BETA"
    }>;
  };
}

// PVQ.ai Project - our reference implementation
export const PVQ_PROJECT: ProjectConfig = {
  id: 'c3e5166d-704c-417d-8969-b3249bf6c66c',  // Matches database
  name: 'PVQ.ai',
  slug: 'pvq-ai',
  description: 'PVQ.ai PI Roadmap - Program Increment Planning',

  features: {
    piAlignment: true,
    systemArchitecture: true,
    auditViewer: true,
    faqAssist: true,
    nocMonitoring: true,
  },

  dataViewer: {
    enabled: true,
    menuLabel: 'Audit Viewer',
    recordTypeLabel: 'Audit Summary',
    dataType: 'pvq-audit',
    samplePath: '/audit-payloads/',
    description: 'View and analyze PVQ audit logs with guardrail trace data',
    samplePayloads: [
      {
        id: 'foreign-contact',
        title: 'Foreign Contact Question',
        description: 'Example of a legitimate user question about foreign contact reporting that passes guardrails',
        icon: 'check',
        color: 'green',
        path: '/audit-payloads/foreign-contact-question.json'
      },
      {
        id: 'guardrail-blocked',
        title: 'Guardrail Intervention',
        description: 'Example of inappropriate content blocked by content policy guardrails',
        icon: 'shield',
        color: 'red',
        path: '/audit-payloads/guardrail-blocked.json'
      }
    ],
  },

  analytics: {
    enabled: true,
    parquetPrefix: 'pvq-ai',
    athenaTable: 'audit_events',
  },

  sidebarSections: {
    enabled: true,
    title: 'PVQ Sections',
    sections: [
      { id: 'passport', label: 'U.S. Passport Information', icon: '📘', order: 1 },
      { id: 'federal-service', label: 'Former Federal Service', icon: '🏛️', order: 2 },
      { id: 'financial-judgment', label: 'Financial - Civil Judgment', icon: '💰', order: 3 },
      { id: 'foreign-contact', label: 'Foreign Contact', icon: '🌍', order: 4 },
      { id: 'review', label: 'Comprehensive Final Review', icon: '💡', order: 5, badge: 'AI' },
      { id: 'poc-settings', label: 'POC Settings', icon: '⚙️', order: 6 },
      { id: 'validator', label: 'PVQ Validator', icon: '✅', order: 7 },
    ],
  },
};

// PDT (Position Designation Tool) Project
export const PDT_PROJECT: ProjectConfig = {
  id: 'b8a35bc4-89d1-4c1b-bbd7-986f707dee3b',  // Matches database
  name: 'PDT',
  slug: 'pdt-project',
  description: 'Position Designation Tool (PDT)',

  features: {
    piAlignment: true,
    systemArchitecture: true,
    auditViewer: true,  // But it shows PDT JSONs, not PVQ audits
    faqAssist: false,   // Maybe later
    nocMonitoring: false,  // Not needed for PDT
  },

  dataViewer: {
    enabled: true,
    menuLabel: 'PDT JSON Viewer',
    recordTypeLabel: 'PDT Record',
    dataType: 'pdt-record',
    samplePath: '/pdt-payloads/',
    description: 'View and analyze Position Designation Tool records',
    samplePayloads: [
      {
        id: 'critical-sensitive',
        title: 'Critical-Sensitive Position',
        description: 'TOPGUN position with Critical-Sensitive designation and High Risk level',
        icon: 'shield',
        color: 'red',
        path: '/pdt-payloads/JSON_1.json'
      },
      {
        id: 'special-sensitive',
        title: 'Special-Sensitive Position',
        description: 'TOPGUN position with Special-Sensitive designation and High Risk level',
        icon: 'document',
        color: 'yellow',
        path: '/pdt-payloads/JSON_3.json'
      },
      {
        id: 'irs-agent',
        title: 'Armed IRS Agent Position',
        description: 'Armed IRS agents position with Special-Sensitive designation',
        icon: 'check',
        color: 'blue',
        path: '/pdt-payloads/JSON_6.json'
      },
      {
        id: 'cfo',
        title: 'CFO Position',
        description: 'Chief Financial Officer position with Special-Sensitive designation',
        icon: 'document',
        color: 'green',
        path: '/pdt-payloads/JSON_8.json'
      }
    ],
  },

  analytics: {
    enabled: true,
    parquetPrefix: 'pdt',
    athenaTable: 'pdt_events',
  },

  sidebarSections: {
    enabled: false,  // PDT doesn't have form sections
    title: 'PDT Sections',
    sections: [],
  },
};

// Project registry - simple array
export const ALL_PROJECTS: ProjectConfig[] = [
  PVQ_PROJECT,
  PDT_PROJECT,
];

// Get project config by ID
export function getProjectConfig(projectId: string | null): ProjectConfig | null {
  if (!projectId) return null;
  return ALL_PROJECTS.find(p => p.id === projectId) || null;
}

// Get project config by slug
export function getProjectConfigBySlug(slug: string): ProjectConfig | null {
  return ALL_PROJECTS.find(p => p.slug === slug) || null;
}
